import { toPercentage, toSignificant } from '@/utilities/formatting';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';
import { useSelector } from 'react-redux';
import { getSummary, getAnalysis } from '@/pages/CompanyReport/redux/selectors';
import { getAveragesSelector } from '@ducks/averages/selectors';
import {
  FutureTranslation,
  useFutureTranslation,
} from '../../hooks/useFutureTranslation';

interface Props {
  companyId: string;
}

const FutureGrowthRatesRawData = ({ companyId }: Props) => {
  const {
    uniqueSymbol,
    analystCount,
    industryData: { secondaryName: industrySecondaryName },
  } = useSelector((state) => getSummary(state, { companyId }));

  const {
    earningsGrowthAnnual: netIncomeGrowthAnnual,
    revenueGrowthAnnual,
    earningsPerShareGrowthAnnual,
  } = useSelector((state) => getAnalysis(state, { companyId }));

  const { getIndustryAverage, getMarketAverage } = useSelector((state) =>
    getAveragesSelector(state, { companyId })
  );
  const industryNetIncomeGrowthAnnual = getIndustryAverage(
    'netIncomeGrowthAnnual'
  );
  const industryAverageRevenueGrowthAnnual = getIndustryAverage(
    'revenueGrowthAnnual'
  ).value;
  const {
    source: industryCountryName,
    value: industryAverageNetIncomeGrowthAnnual,
  } = industryNetIncomeGrowthAnnual;
  const {
    source: marketCountryName,
    value: marketAverageNetIncomeGrowthAnnual,
  } = getMarketAverage('netIncomeGrowthAnnual');
  const marketAverageRevenueGrowthAnnual = getMarketAverage(
    'revenueGrowthAnnual'
  ).value;

  const t = useFutureTranslation();

  return (
    <>
      <DataTable columnWidths="4, 4, 2" light>
        <caption>{t('analyst.table.description', { uniqueSymbol })}</caption>
        <thead>
          <tr>
            <th>{t('analyst.table.header.dataPoint')}</th>
            <th>{t('analyst.table.header.source')}</th>
            <th>{t('analyst.table.header.valuePerYear')}</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            if (netIncomeGrowthAnnual)
              return (
                <tr>
                  <td>{t('analyst.table.futureEarnings', { uniqueSymbol })}</td>
                  <td>
                    <FutureTranslation
                      i18nKey="analyst.table.futureEarnings.lineOfBestFit"
                      values={{ count: toSignificant(analystCount, 0) }}
                    >
                      <>
                        <span>
                          Line of Best Fit<sup>*</sup>
                        </span>
                        <span>
                          through Consensus Estimate Earnings of{' '}
                          {toSignificant(analystCount, 0)} Analysts
                        </span>
                      </>
                    </FutureTranslation>
                  </td>
                  <td>{toPercentage(netIncomeGrowthAnnual, 1)}</td>
                </tr>
              );
            else if (earningsPerShareGrowthAnnual)
              return (
                <tr>
                  <td>
                    {t('analyst.table.earningsPerShare', { uniqueSymbol })}
                  </td>
                  <td>
                    <FutureTranslation
                      i18nKey="analyst.table.earningsPerShare.lineOfBestFit"
                      values={{ count: toSignificant(analystCount, 0) }}
                    >
                      <>
                        <span>
                          Line of Best Fit<sup>*</sup>
                        </span>
                        <span>
                          through Consensus Estimate Revenue of{' '}
                          {toSignificant(analystCount, 0)} Analysts
                        </span>
                      </>
                    </FutureTranslation>
                  </td>
                  <td>{toPercentage(earningsPerShareGrowthAnnual, 1)}</td>
                </tr>
              );
          })()}
          {revenueGrowthAnnual && (
            <tr>
              <td>{t('analyst.table.revenueGrowth', { uniqueSymbol })}</td>
              <td>
                <FutureTranslation
                  i18nKey="analyst.table.revenueGrowth.lineOfBestFit"
                  values={{ count: toSignificant(analystCount, 0) }}
                >
                  <>
                    <span>
                      Line of Best Fit<sup>*</sup>
                    </span>
                    <span>
                      through Consensus Estimate Revenue of{' '}
                      {toSignificant(analystCount, 0)} Analysts
                    </span>
                  </>
                </FutureTranslation>
              </td>
              <td>{toPercentage(revenueGrowthAnnual, 1)}</td>
            </tr>
          )}
          <tr>
            <td>
              {t('analyst.table.industryEarnings', {
                country: industryCountryName,
                secondary: industrySecondaryName,
              })}
            </td>
            <td>{t('analyst.table.marketCap')}</td>
            <td>{toPercentage(industryAverageNetIncomeGrowthAnnual, 1)}</td>
          </tr>
          <tr>
            <td>
              {t('analyst.table.industryRevenue', {
                country: industryCountryName,
                secondary: industrySecondaryName,
              })}
            </td>
            <td>{t('analyst.table.marketCap')}</td>
            <td>{toPercentage(industryAverageRevenueGrowthAnnual, 1)}</td>
          </tr>
          <tr>
            <td>
              {t('analyst.table.marketEarnings', { market: marketCountryName })}
            </td>
            <td>{t('analyst.table.marketCap')}</td>
            <td>{toPercentage(marketAverageNetIncomeGrowthAnnual, 1)}</td>
          </tr>
          <tr>
            <td>
              {t('analyst.table.marketRevenue', { market: marketCountryName })}
            </td>
            <td>{t('analyst.table.marketCap')}</td>
            <td>{toPercentage(marketAverageRevenueGrowthAnnual, 1)}</td>
          </tr>
        </tbody>
      </DataTable>
      <FutureTranslation i18nKey="analyst.table.lineOfBestFit">
        <p>
          <span>
            <sup>*</sup>
            Line of best fit is calculated by
          </span>
          <a
            href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#future-performance`}
            data-unique-symbol={uniqueSymbol}
            data-about-section="future_growth_rates"
            data-link-name="model_linear_regression"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            linear regression
          </a>
          .
        </p>
        <p>
          <a
            href={`${SWS_ANALYSIS_MODEL}/industry_averages.markdown`}
            data-unique-symbol={uniqueSymbol}
            data-about-section="future_growth_rates"
            data-link-name="model_industry_averages"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Industry and Market average data
          </a>
          <span> is calculated daily.</span>
        </p>
      </FutureTranslation>
    </>
  );
};

export default FutureGrowthRatesRawData;
